import { RootElement } from '../form_elements/RootElement';
import { isEmpty, isNullOrUndefined } from '../utils/equality';

export default interface FormProps {
  id?: string;
  title: string;
  instructions: string;
  body: RootElement;
}

/**
 * Parts of the form object that are not updated
 * automatically when a user edits.
 */
export interface FormConfigProps {
  enable_email_copy_of_response: boolean;
  state: PublishStatusProps;
  schedule: PublishScheduleProps;
}

export interface FormUpdateResponseData {
  success: boolean;
  version: number;
  state: PublishStatusProps;
  schedule: PublishScheduleProps;
  enable_email_copy_of_response: boolean;
  errors: FormErrors;
}

export enum PublishStatusProps {
  Published = 'published',
  Unpublished = 'unpublished',
  Closed = 'closed'
}

export enum FormStateActions {
  MANUAL_PUBLISH = 'manual_publish',
  MANUAL_UNPUBLISH = 'manual_unpublish',
  SCHEDULE_PUBLISH = 'schedule_publish',
  SCHEDULE_UNPUBLISH = 'schedule_unpublish',
  OVERRIDE_SCHEDULE = 'override_schedule',
  UNDO_SCHEDULE_PUBLISH = 'undo_schedule_publish',
  UNDO_SCHEDULE_UNPUBLISH = 'undo_schedule_unpublish'
}

export type UpdateScheduleProps =
{
  action: FormStateActions.MANUAL_PUBLISH | FormStateActions.MANUAL_UNPUBLISH
} | {
  action: FormStateActions.SCHEDULE_PUBLISH,
  publish_datetime: string,
} | {
  action: FormStateActions.SCHEDULE_UNPUBLISH,
  unpublish_datetime: string
} | {
  action: FormStateActions.OVERRIDE_SCHEDULE,
  publish_datetime: string,
  unpublish_datetime: string
} | {
  action: FormStateActions.UNDO_SCHEDULE_PUBLISH | FormStateActions.UNDO_SCHEDULE_UNPUBLISH
};

export type UpdateScheduleResponse =
{
  success: true,
  version: number,
  state: PublishStatusProps,
  schedule: PublishScheduleProps
} | {
  success: false,
  version: number,
  errors: FormErrors
};

export interface PublishScheduleProps {
  publish_datetime: string | null,
  unpublish_datetime: string | null,
}

export enum AudienceType {
  PUBLIC = 'FormAudience::Public',
  STUDENT = 'FormAudience::Student',
  UNTARGETED_STUDENT = 'FormAudience::UntargetedStudent',
  STAFF = 'FormAudience::Staff',
  UNTARGETED_STAFF = 'FormAudience::UntargetedStaff',
  PARENT = 'FormAudience::Parent',
  UNTARGETED_PARENT = 'FormAudience::UntargetedParent'
}

export type AudienceProps =
  | PublicAudienceProps
  | UntargetedStaffAudienceProps
  | StaffAudienceProps
  | UntargetedStudentAudienceProps
  | StudentAudienceProps
  | UntargetedParentAudienceProps
  | ParentAudienceProps;

export interface PublicAudienceProps {
  type: AudienceType.PUBLIC;
}

export function newPublicAudienceProps() : PublicAudienceProps {
  return {
    type: AudienceType.PUBLIC
  };
}

export interface UntargetedStaffAudienceProps {
  type: AudienceType.UNTARGETED_STAFF;
}

export function newUntargetedStaffAudienceProps() : UntargetedStaffAudienceProps {
  return {
    type: AudienceType.UNTARGETED_STAFF
  };
}

export interface StaffAudienceProps {
  type: AudienceType.STAFF;
  entities: Array<School | Staff>;
}

export function newStaffAudienceProps(entities?: Array<School | Staff>) : StaffAudienceProps {
  return {
    type: AudienceType.STAFF,
    entities: entities || []
  };
}

export interface UntargetedStudentAudienceProps {
  type: AudienceType.UNTARGETED_STUDENT;
}

export function newUntargetedStudentAudienceProps() : UntargetedStudentAudienceProps {
  return {
    type: AudienceType.UNTARGETED_STUDENT
  };
}

export interface StudentAudienceProps {
  type: AudienceType.STUDENT;
  year: number;
  entities: Array<School | Level | Clazz | TeachingGroup | Student>;
}

export function newStudentAudienceProps({ entities, year }: { entities?: Array<School | Level | Clazz | TeachingGroup | Student>; year?: number }): StudentAudienceProps {
  return {
    type: AudienceType.STUDENT,
    entities: entities || [],
    year: year || workingYear(new Date())
  };
}

export interface UntargetedParentAudienceProps {
  type: AudienceType.UNTARGETED_PARENT;
}

export function newUntargetedParentAudienceProps() : UntargetedParentAudienceProps {
  return {
    type: AudienceType.UNTARGETED_PARENT
  };
}

export interface ParentAudienceProps {
  type: AudienceType.PARENT;
  year: number;
  entities: Array<School | Level | Clazz | TeachingGroup | Student>;
}

export function newParentAudienceProps({ entities, year }: { entities?: Array<School | Level | Clazz | TeachingGroup | Student>; year?: number }) : ParentAudienceProps {
  return {
    type: AudienceType.PARENT,
    entities: entities || [],
    year: year || workingYear(new Date())
  };
}

export function isUntargetedAudience(audience: AudienceProps): audience is UntargetedStaffAudienceProps | UntargetedStudentAudienceProps | UntargetedParentAudienceProps {
  return audience.type === AudienceType.UNTARGETED_STAFF || audience.type === AudienceType.UNTARGETED_STUDENT || audience.type === AudienceType.UNTARGETED_PARENT;
}

function workingYear(date: Date) {
  const month = date.getMonth() + 1; // getMonth() is zero-based
  const year = date.getFullYear();

  return month < 12 ? year : year + 1; // if before Dec, return curr year, else return next year
}

export enum StaffEntityType {
  SCHOOL = 'school',
  STAFF = 'staff'
}

export enum StudentEntityType {
  TEACHING_GROUP = 'teaching_group',
  CLAZZ = 'clazz',
  LEVEL = 'level',
  SCHOOL = 'school',
  STUDENT = 'student'
}

export interface Entity {
  entity_id: number;
  entity_type: StudentEntityType | StaffEntityType;
}

// The audience scope constraint carried by the form's template, read live by
// the server on every page load. An absent field is unrestricted; the whole
// object is null for a form with no template or a template with no constraint.
export interface ScopeConstraints {
  // The audience type the template pins its forms to. Read here only to name
  // the pin to the form creator: the pin the steps themselves obey is resolved
  // server-side across both sources and arrives as `expectedAudience`.
  audienceType?: AudienceType;
  allowedEntityTypes?: Array<StudentEntityType>;
  allowedLevelCodes?: Array<number>;
}

export function isEntityEmpty(entity: Entity): boolean {
  return isEmpty(entity) || isNullOrUndefined(entity.entity_id);
}

export interface School extends Entity {
  name: string;
}

export interface Level extends Entity {
  code: number;
  name?: string;
  school_id: number;
}

export interface Clazz extends Entity {
  name: string;
  level_id: number;
  level_code: number;
  clazz_code: string;
  school_id: number;
}

export interface TeachingGroup extends Entity {
  name: string;
  level_id: number;
  level_code: number;
  school_id: number;
  subject_description: Subject;
}

export type Subject = string;

export interface Student extends Entity {
  name: string;
  level_id: number;
  level_code: number;
  clazz_code: string;
  clazz_id: number;
  clazz_name: string;
  index_number: number;
  school_id: number;
  teaching_group_ids: Set<number>;
}

export interface Staff extends Entity {
  name: string;
  email: string;
  school: School;
}

export interface StudentRoster {
  year: number;
  school: School;
  levels: Array<Level>;
  clazzes: Array<Clazz>;
  teachingGroups: Array<TeachingGroup>;
  students: Array<Student>;
}

export interface StaffRoster {
  school: School;
  staff: Array<Staff>;
}

export interface FormErrors {
  title?: Array<string>;
  instructions?: Array<string>;
  body?: Array<{
    element_id: string;
    errors: FormErrorsByElementAttribute;
  }>;
  bodyByElementID?: FormErrorsByElementID;
  state?: Array<string>;
  audience?: Array<string>;
  audience_metadata?: Array<string>;
  version?: string;
  schedule?: Array<string>;
}

export type FormErrorsByElementID = { [elementID: string]: FormErrorsByElementAttribute };

export type FormErrorsByElementAttribute = { [attribute: string]: Array<string> };

export interface FormAnswer {
  question_id: string;
  values: Array<any>;
  errors?: FormAnswerErrorsByAttribute;
}

export type FormAnswerByQnID = { [question_id: string]: FormAnswer };

export type ResponsesByOptionID = { [option_id: string]: any };

export type QuestionOptionsResponsesByQnID = { [question_id: string] : ResponsesByOptionID };

export type LimitedResponsesErrorsByQnID = { [question_id: string] : string };

export type FormAnswerErrorsByAttribute = { [attr: string]: Array<string> };
